import { Modal, StyleSheet, TouchableOpacity, View, Text } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { router } from 'expo-router'
import { Section } from '@/src/common/components/Section'
import { ModalMenuScrollView } from '@/src/app/main/menu'
import { ChevronRight } from '@/src/assets/icons/ChevronRight'
import { mesaColorNames, typography } from '@/src/common/theme'
import { useState } from 'react'
import { upperFirst } from 'lodash'
import { NotificationComponents } from '../components'
import { GET_NOTIFICATION_SETTINGS } from '../gql/queries'
import { useLoggedInQuery } from '@/src/common/gql/MesaGql'
import type { NotificationSettings } from '@api/mesa/graphql'
import { NotificationsRoutes } from '../navigation/routes'
import { MesaToast } from '@/src/common/components/MesaToast'

export type Category = Exclude<keyof NotificationSettings, '__typename'>

function SelectedNotificationCategorySettings({ settings }: { settings: NotificationSettings }) {
  const [selectedNotificationCategory, setSelectedNotificationCategory] = useState<Category>()

  function onClose() {
    router.dismiss()
    router.navigate(NotificationsRoutes.Center.route)
  }

  return (
    <Modal transparent visible animationType="fade">
      <SafeAreaView
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <TouchableOpacity
          activeOpacity={1}
          onPress={onClose}
          style={[StyleSheet.absoluteFillObject, { flex: 1, backgroundColor: '#00000050' }]}
        />

        <NotificationComponents.CategorySettings
          categoryName={selectedNotificationCategory}
          settings={settings}
        >
          {({ categorySettings, updateButton }) => (
            <>
              <ModalMenuScrollView contentContainerStyle={{ height: '100%' }} footer={updateButton}>
                <Section
                  title={
                    selectedNotificationCategory
                      ? upperFirst(selectedNotificationCategory)
                      : `Notification Settings`
                  }
                  titleStyles={[
                    {
                      color: mesaColorNames['Mesa - Brand/Secondary #223843'],
                      marginLeft: 0,
                    },
                    typography['label/lg-bold'],
                  ]}
                  styleOverride={{ marginBottom: 0 }}
                  style={{
                    marginBottom: 0,
                    width: '100%',
                    minHeight: 48,
                    backgroundColor: mesaColorNames['Greyscale/0 #FFFFFF'],
                    paddingTop: 24,
                    paddingBottom: 8,
                    paddingHorizontal: 0,
                  }}
                  onClosePress={() =>
                    selectedNotificationCategory
                      ? setSelectedNotificationCategory(undefined)
                      : onClose()
                  }
                  closeStyle={{ right: 0, position: 'absolute' }}
                />

                <View style={{ display: 'flex', flexDirection: 'column' }}>
                  {selectedNotificationCategory ? (
                    categorySettings
                  ) : (
                    <>
                      <CategorySettingsRow
                        category="card"
                        setSelectedNotificationCategory={setSelectedNotificationCategory}
                      />
                      <CategorySettingsRow
                        category="marketing"
                        setSelectedNotificationCategory={setSelectedNotificationCategory}
                      />
                      <CategorySettingsRow
                        category="announcements"
                        setSelectedNotificationCategory={setSelectedNotificationCategory}
                      />
                    </>
                  )}
                </View>
              </ModalMenuScrollView>
              <MesaToast />
            </>
          )}
        </NotificationComponents.CategorySettings>
      </SafeAreaView>
    </Modal>
  )
}

const CategorySettingsRow = ({
  category,
  setSelectedNotificationCategory,
}: {
  setSelectedNotificationCategory: (category: Category) => void
  category: Category
}) => (
  <TouchableOpacity
    style={{
      paddingVertical: 6,
      marginBottom: 0,
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    }}
    testID={category}
    key={category}
    onPress={() => setSelectedNotificationCategory(category)}
  >
    <Text
      style={{
        color: mesaColorNames['Mesa - Brand/Blackish #222222'],
        fontSize: 16,
      }}
    >
      {upperFirst(category)}
    </Text>
    <ChevronRight style={{ flexShrink: 0 }} color={mesaColorNames['semantic/fg/base #131214']} />
  </TouchableOpacity>
)

const Settings = () => {
  const { data: getNotificationSettings, loading: getNotificationSettingsIsLoading } =
    useLoggedInQuery(GET_NOTIFICATION_SETTINGS)

  if (getNotificationSettingsIsLoading || !getNotificationSettings) {
    return null
  }

  return (
    <SelectedNotificationCategorySettings
      settings={getNotificationSettings.getNotificationSettings.settings}
    />
  )
}

export const SettingsScreen = {
  name: 'notifications-settings',
  Screen: Settings,
} as const
